"use client";

import * as React from "react";
import { Star, Quote, Check, Minus, X } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
const carouselImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/automated_roller_shades.png",
  alt: "Automated roller shades in a modern living room"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/smart_cellular_blinds.png",
  alt: "Energy efficient smart cellular blinds in a bedroom"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/solar_screen_patio.png",
  alt: "Motorized solar screens enclosing a patio area"
}];
const reviews = [{
  id: 1,
  author: "James Wilson",
  rating: 5,
  text: "The automated roller shades have drastically reduced our afternoon cooling costs. The app integration is flawless and they look incredibly sleek.",
  location: "Austin, TX"
}, {
  id: 2,
  author: "Elena Rodriguez",
  rating: 5,
  text: "We installed smart cellular blinds in the bedroom. The insulation improvement is noticeable immediately, and waking up to natural light on a schedule is amazing.",
  location: "Denver, CO"
}, {
  id: 3,
  author: "Michael Chang",
  rating: 5,
  text: "Our new patio solar screens make the outdoor space usable even on the hottest days. Great quality materials and the installation team was very professional.",
  location: "Phoenix, AZ"
}];
const comparisonData = [{
  id: 1,
  feature: "Roller Shades",
  cloud: "Full",
  local: "Yes",
  energy: "High",
  status: "check"
}, {
  id: 2,
  feature: "Cellular Blinds",
  cloud: "Full",
  local: "Yes",
  energy: "Premium",
  status: "orange"
}, {
  id: 3,
  feature: "Solar Screens",
  cloud: "Partial",
  local: "No",
  energy: "Standard",
  status: "red"
}];
export default function ClimateWindowCoveringsReviews() {
  return <section id="climate-window-coverings-reviews" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 text-center lg:text-left">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Customer Experiences
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0">
            See how our climate-responsive window coverings are transforming homes, improving energy efficiency, and enhancing daily comfort.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="w-full max-w-xl mx-auto lg:max-w-none px-12 sm:px-16 lg:px-12">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                {carouselImages.map((image, i) => <CarouselItem key={image.id} data-index={i}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-lg">
                      <img src={image.src} alt={image.alt} className="object-cover w-full h-full transition-transform duration-500 hover:scale-105" />
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex -left-12 lg:-left-6 bg-background/80 backdrop-blur-sm hover:bg-background" />
              <CarouselNext className="hidden sm:flex -right-12 lg:-right-6 bg-background/80 backdrop-blur-sm hover:bg-background" />
            </Carousel>

            <div className="mt-12 overflow-x-auto rounded-2xl border border-border/50 bg-muted/20">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Feature</th>
                    <th className="px-6 py-4 font-semibold">Cloud Control</th>
                    <th className="px-6 py-4 font-semibold">Local Control</th>
                    <th className="px-6 py-4 font-semibold">Energy Data</th>
                    <th className="px-6 py-4 font-semibold text-center">Marks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {comparisonData.map((row, i) => <tr key={row.id} data-index={i} className="hover:bg-muted/10 transition-colors">
                      <td className="px-6 py-4 font-medium text-foreground">{row.feature}</td>
                      <td className="px-6 py-4 text-muted-foreground">{row.cloud}</td>
                      <td className="px-6 py-4 text-muted-foreground">{row.local}</td>
                      <td className="px-6 py-4 text-muted-foreground">{row.energy}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          {row.status === "check" && <Check className="size-5 text-green-600" />}
                          {row.status === "orange" && <Minus className="size-5 text-orange-500" />}
                          {row.status === "red" && <X className="size-5 text-destructive" />}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {reviews.map((review, i) => <Card key={review.id} data-index={i} className="bg-muted/30 border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <Quote className="size-8 text-primary/20 shrink-0 mt-1" />
                    <div className="space-y-4">
                      <div className="flex items-center gap-1">
                        {[...Array(review.rating)].map((_, starIndex) => <Star key={starIndex} className="size-4 fill-primary text-primary" />)}
                      </div>
                      <p className="text-foreground leading-relaxed italic">
                        "{review.text}"
                      </p>
                      <div>
                        <p className="font-semibold font-default text-foreground">
                          {review.author}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {review.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}